/**
 * Énumérations partagées entre l'API et le front.
 *
 * Toute valeur définie ici doit exister à l'identique dans `schema.prisma`
 * (même nom d'enum, mêmes membres) : c'est le contrat entre la base et l'UI.
 */

/** Rôles applicatifs. Un compte peut en porter plusieurs (union des droits). */
export enum UserRole {
  /** Transverse. Paramétrage, comptes, accès total. */
  ADMIN = 'ADMIN',
  /** Transverse. Pilote les collectes, exploite les données, produit les rapports. */
  ANALYSTE = 'ANALYSTE',
  /** Borné aux familles d'artisanat qui lui sont assignées. */
  CONSEILLER = 'CONSEILLER',
  /** Transverse, lecture seule : tableaux de bord et consultation des données. */
  OBSERVATEUR = 'OBSERVATEUR',
}

/** Origine du compte : locale, ou fournie par un annuaire externe. */
export enum AuthSource {
  LOCAL = 'LOCAL',
  OIDC = 'OIDC',
  LDAP = 'LDAP',
}

/**
 * Familles d'artisanat. Unique dimension de cloisonnement métier :
 * un CONSEILLER ne voit que les familles qui lui sont rattachées.
 */
export enum FamilleArtisanat {
  TAPIS = 'TAPIS',
  POTERIE = 'POTERIE',
  MAROQUINERIE = 'MAROQUINERIE',
  TEXTILE = 'TEXTILE',
  VANNERIE = 'VANNERIE',
  BOIS = 'BOIS',
  METAL = 'METAL',
  BIJOUX = 'BIJOUX',
  COSMETIQUE = 'COSMETIQUE',
  VETEMENT = 'VETEMENT',
  AUTRE = 'AUTRE',
}

/** Cycle de vie d'une exécution de collecte. */
export enum StatutCollecte {
  PLANIFIEE = 'PLANIFIEE',
  EN_COURS = 'EN_COURS',
  TERMINEE = 'TERMINEE',
  /** Interrompue proprement faute de quota : les données acquises sont conservées. */
  QUOTA_EPUISE = 'QUOTA_EPUISE',
  ERREUR = 'ERREUR',
  ANNULEE = 'ANNULEE',
}

/**
 * Cycle de vie d'une configuration de collecte.
 *
 * `EN_PAUSE` : lançable à la main, ignorée par l'ordonnanceur. C'est l'état
 * de naissance des requêtes générées depuis les tags. `ACTIVE` : planifiée.
 * `ARCHIVEE` : conservée pour l'historique, plus jamais lancée.
 */
export enum StatutConfigCollecte {
  EN_PAUSE = 'EN_PAUSE',
  ACTIVE = 'ACTIVE',
  ARCHIVEE = 'ARCHIVEE',
}

/** Provenance d'une configuration de collecte. */
export enum OrigineConfigCollecte {
  /** Saisie dans le formulaire. */
  MANUELLE = 'MANUELLE',
  /** Générée par l'inventaire des tags du catalogue : un couple tag × famille. */
  TAGS = 'TAGS',
  /** Générée pour une petite famille : les annonces d'une catégorie qu'elle domine. */
  CATEGORIE = 'CATEGORIE',
  /** Générée pour une petite famille : le catalogue d'une boutique qui lui est spécialisée. */
  BOUTIQUE = 'BOUTIQUE',
}

/** Stratégie de l'ordonnanceur pour choisir les prochaines requêtes à lancer. */
export enum ModeOrdonnanceur {
  /** Chaque requête active dotée d'une cadence est relancée selon son propre délai. */
  CADENCE = 'CADENCE',
  /** Parcourt actives et générées en pause, la moins récemment collectée d'abord. */
  BOUCLE = 'BOUCLE',
  /** Répartit les lancements entre familles, à l'inverse de leur part du catalogue. */
  COUVERTURE = 'COUVERTURE',
}

/** Étapes d'une collecte, dans l'ordre où elles s'enchaînent. */
export enum EtapeCollecte {
  RECHERCHE = 'RECHERCHE',
  DETAILS = 'DETAILS',
  VARIANTES = 'VARIANTES',
  AVIS = 'AVIS',
  TAXONOMIE = 'TAXONOMIE',
  SECTIONS = 'SECTIONS',
  ATTRIBUTS = 'ATTRIBUTS',
  ARTICLES_VENDUS = 'ARTICLES_VENDUS',
}

/** Cycle de vie d'un rapport avancé généré par IA. */
export enum StatutRapportAvance {
  GENERATION = 'GENERATION',
  PRET = 'PRET',
  ERREUR = 'ERREUR',
}

/** Sévérité d'une notification, alignée sur les couleurs sémantiques du design system. */
export enum NiveauNotification {
  INFO = 'INFO',
  SUCCES = 'SUCCES',
  AVERTISSEMENT = 'AVERTISSEMENT',
  ERREUR = 'ERREUR',
}
